import React, { useState, useEffect } from 'react';
import {
  describeBankLoadFailure, bankPickerPlaceholder, describeBankListNotice, autoPickBankId,
} from './bankPickerAccess';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversity, faMoneyCheckAlt, faCalendarAlt, faBox } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import ReportingMonthInput from '../../components/Invoicing/ReportingMonthInput';
import SearchableSelect from '../../components/Invoicing/SearchableSelect';
import InvoiceNotesField from '../../components/Invoicing/InvoiceNotesField';
import { bankAccountLabel } from '../../utils/bankLabel';
import './OutgoingInvoice.css';
import { costCentreLabel } from '../../utils/costCentreLabel';

/**
 * Screen 3: Bank Details, Cost Centre, Notes & Workflow for INCOMING INVOICES
 * 
 * Fields:
 * - Bank Account Details (dropdown from SUPPLIER's banking details - where HMS pays TO)
 * - Cost Centre (dropdown from accounting)
 * - Reporting Month (calendar month-year)
 * - Invoice Notes (multi-line text)
 * - Save Draft / Submit for Approval buttons
 * 
 * Note: No "Notes Visible on Invoice" checkbox for incoming invoices
 */

const IncomingInvoiceScreen3 = ({ 
  invoiceData, 
  setInvoiceData,
  lineItems,
  vesselInfo,
  onPrevious,
  isReadOnly = false
}) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const costCenterParams = invoiceData.hms_entity ? { entity: invoiceData.hms_entity } : {};
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  // Why the list is empty, when it is. See ./bankPickerAccess.js — a
  // refusal and a genuinely empty list used to render identically.
  const [bankLoadFailure, setBankLoadFailure] = useState(null);
  
  // Load banking details when partner changes (for incoming invoices, we need supplier's bank details)
  useEffect(() => {
    if (invoiceData.partner) {
      loadBankingDetails();
    } else {
      setBankAccounts([]);
    }
  }, [invoiceData.partner]);
  
  const loadBankingDetails = async () => {
    if (!invoiceData.partner) {
      setBankAccounts([]);
      return;
    }
    
    setLoadingBankAccounts(true);
    try {
      const response = await invoicingService.fetchBankingDetails(
        'INCOMING',
        null,
        invoiceData.partner
      );
      
      const banks = response.banking_details || [];
      setBankAccounts(banks);
      setBankLoadFailure(describeBankListNotice(response));
      
      // Fill the box only when the server says there is nothing to guess (the
      // one main account, or the only one). See ./bankPickerAccess.js.
      const pickId = autoPickBankId(response);
      if (pickId && !invoiceData.supplier_bank_account) {
        setInvoiceData(prev => ({ ...prev, supplier_bank_account: pickId }));
      }
      
      console.log(`Loaded ${banks.length} banking details for partner ${invoiceData.partner}`);
    } catch (error) {
      console.error('Error loading banking details:', error);
      setBankAccounts([]);
      setBankLoadFailure(describeBankLoadFailure(error));
    } finally {
      setLoadingBankAccounts(false);
    }
  };
  
  return (
    <div className="invoice-screen-content">
      <div className="invoice-form-section">
        <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <FontAwesomeIcon icon={faUniversity} /> Bank Details, Cost Centre & Notes
          </span>
          <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
            {invoiceData.invoice_number ? `Invoice: ${invoiceData.invoice_number}` : '(number will be generated on save)'}
          </span>
        </h2>
        
        <div className="invoice-form-grid">
          {/* Left Column - All Fields */}
          <div className="invoice-form-column">
            {/* Bank Account Details (Supplier's Bank) */}
            <div className="form-field">
              <label className="field-label">
                <FontAwesomeIcon icon={faUniversity} /> Supplier Bank Account Details
                {loadingBankAccounts && <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#888' }}>(Loading...)</span>}
              </label>
              <select
                id="bank_account_select"
                className="form-select"
                value={invoiceData.supplier_bank_account || ''}
                onChange={(e) => {
                  setInvoiceData(prev => ({ ...prev, supplier_bank_account: e.target.value }));
                  clearFieldError('bank_account_select');
                }}
                disabled={loadingBankAccounts || !invoiceData.partner}
              >
                <option value="">
                  {bankPickerPlaceholder({
                    hasParent: Boolean(invoiceData.partner),
                    parentLabel: 'Supplier',
                    withheld: Boolean(bankLoadFailure?.withheld),
                    failed: Boolean(bankLoadFailure && !bankLoadFailure.withheld && !bankLoadFailure.notice),
                    count: bankAccounts.length,
                  })}
                </option>
                {bankAccounts.map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bankAccountLabel(bank)}
                    {bank.is_primary_bank ? ' (Primary)' : ''}
                  </option>
                ))}
              </select>
            {/* Why the picker is unusable, in words, on the screen. An empty
                dropdown alone reads as "this entity has no bank accounts" and
                sends someone hunting for data that was simply withheld. */}
            {bankLoadFailure && (
              <p
                className="field-hint"
                role={bankLoadFailure.withheld || bankLoadFailure.notice ? undefined : 'alert'}
                style={{
                  marginTop: '6px',
                  fontSize: '0.85em',
                  color: bankLoadFailure.withheld || bankLoadFailure.notice ? '#5a6b78' : '#a62a20',
                }}
              >
                {bankLoadFailure.message}
              </p>
            )}
            </div>
            
            {/* Cost Centre (was labelled "Cost Allocation"; Gritt could not find it) */}
            <div className="form-field">
              <label className="field-label">
                <FontAwesomeIcon icon={faMoneyCheckAlt} /> Cost Centre
              </label>
              <SearchableSelect
                fetchUrl="/api/invoicing/cost-centers/"
                params={costCenterParams}
                displayFn={costCentreLabel}
                value={invoiceData.cost_center || ''}
                onChange={(id) => {
                  setInvoiceData(prev => ({ ...prev, cost_center: id || '' }));
                  clearFieldError('cost_center_select');
                }}
                placeholder="Select Cost Centre..."
                disabled={isReadOnly}
                variant="form"
                id="cost_center_select"
                listOnOpen
                pageSize={50}
              />
            </div>
            
            {/* Cost Category */}
            <div className="form-field">
              <label className="field-label">
                <FontAwesomeIcon icon={faMoneyCheckAlt} /> Cost Category
              </label>
              <SearchableSelect
                fetchUrl="/api/invoicing/cost-categories/"
                displayFn={(cat) => `${cat.code} - ${cat.short_name}`}
                value={invoiceData.cost_category || ''}
                onChange={(id) => {
                  setInvoiceData(prev => ({ ...prev, cost_category: id || '' }));
                  clearFieldError('cost_category_select');
                }}
                placeholder="Select Cost Category..."
                disabled={isReadOnly}
                variant="form"
                id="cost_category_select"
                listOnOpen
                pageSize={50}
              />
            </div>
            
            {/* Reporting Month */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faCalendarAlt} /> Reporting Month
              </label>
              <ReportingMonthInput
                id="reporting_month_input"
                className="form-input"
                value={invoiceData.reporting_month || ''}
                entityId={invoiceData.hms_entity}
                onChange={(value) => {
                  setInvoiceData(prev => ({ ...prev, reporting_month: value }));
                }}
                onClear={() => clearFieldError('reporting_month_input')}
                disabled={isReadOnly}
              />
            </div>
          </div>
          
          {/* Right Column - Notes Full Height */}
          <div className="invoice-form-column">
            {/* Internal notes only - incoming invoices have no customer-facing PDF,
                so there is no "visible on the printout" tick here. */}
            <InvoiceNotesField
              id="incoming_invoice_notes"
              label="Invoice Notes"
              value={invoiceData.notes}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Enter invoice notes (internal use only)..."
              isReadOnly={isReadOnly}
              fillHeight
            />
          </div>
        </div>
        
        {/* Navigation - Only Previous button (Save/Submit are at page top) */}
        <div className="invoice-workflow-buttons">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={onPrevious}
          >
            <FontAwesomeIcon icon={faBox} /> Previous: Products
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingInvoiceScreen3;

